import { Injectable } from '@angular/core';
import { OrganizationType } from '@portal/api/enums/organization-type';
import { Org } from '@portal/api/responses/organizations/org-details-response';
import { AppContextService } from '@portal/shared/services/app-context.service';
import { OrganizationSelectorService } from '@portal/shared/services/organization-selector.service';
import { StorageService } from '@portal/shared/services/storage.service';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class OrganizationYearContextService {
    private readonly _selectedYear = new BehaviorSubject<number | null>(null);
    private readonly _availableYears = new BehaviorSubject<number[]>([]);
    private readonly _yearChanged = new Subject<number>();

    constructor(
        private readonly _organizationSelectorService: OrganizationSelectorService,
        private readonly _storageService: StorageService,
        private readonly _appContextService: AppContextService,
    ) {
        this._organizationSelectorService.orgState$
            .subscribe((org) => {
                this.syncState(org);
            });
    }

    get selectedYear(): number | null {
        return this._selectedYear.getValue();
    }

    get selectedYear$(): Observable<number | null> {
        return this._selectedYear.asObservable();
    }

    get availableYears(): number[] {
        return this._availableYears.getValue();
    }

    get availableYears$(): Observable<number[]> {
        return this._availableYears.asObservable();
    }

    get yearChanged$(): Observable<number> {
        return this._yearChanged.asObservable();
    }

    setSelectedYear(year: number): void {
        const selectedOrg = this._organizationSelectorService.org;
        const availableYears = this.availableYears;

        if (!this.shouldManageYearForOrg(selectedOrg) || !availableYears.includes(year)) {
            return;
        }

        if (this._selectedYear.getValue() === year) {
            return;
        }

        this._storageService.setOrganizationYear(selectedOrg.id, year);
        this._selectedYear.next(year);
        this._yearChanged.next(year);
    }

    private syncState(org: Org | null): void {
        if (!this.shouldManageYearForOrg(org)) {
            this._availableYears.next([]);
            this._selectedYear.next(null);
            return;
        }

        const availableYears = this.buildAvailableYears(org);
        const storedYear = this._storageService.getOrganizationYear(org.id);
        const fallbackYear = availableYears[0] ?? new Date().getFullYear();
        const selectedYear = storedYear && availableYears.includes(storedYear)
            ? storedYear
            : fallbackYear;

        this._availableYears.next(availableYears);
        this._storageService.setOrganizationYear(org.id, selectedYear);
        this._selectedYear.next(selectedYear);
    }

    private buildAvailableYears(org: Org): number[] {
        const currentYear = new Date().getFullYear();
        const createdOn = org.createdOn ? new Date(org.createdOn) : null;
        const createdYear = createdOn && !Number.isNaN(createdOn.getTime())
            ? createdOn.getFullYear()
            : currentYear;
        const startYear = Math.min(createdYear, currentYear);
        const years: number[] = [];

        for (let year = currentYear; year >= startYear; year -= 1) {
            years.push(year);
        }

        return years;
    }

    private shouldManageYearForOrg(org: Org | null): boolean {
        if (!org || org.type !== OrganizationType.Association) {
            return false;
        }

        try {
            return this._appContextService.getContext() === 'club';
        } catch {
            return false;
        }
    }
}
